// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/collectRailContent.test.ts
import {
  type AskPunttChat,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";
import {
  type AskPunttProject,
  type AskPunttProjectInstruction,
} from "@mg/schemas/src/christo/askPunttProject";
import {
  defaultAskPunttConnectors,
  defaultAskPunttSkills,
} from "@mg/schemas/src/commons/enterprise";

import { isReviewStartUserMessage } from "./isReviewStartUserMessage";
import {
  type AskV2RailConnector,
  type AskV2RailContent,
  type AskV2RailDocument,
  type AskV2RailMemory,
  type AskV2RailMemoryScope,
  type AskV2RailOutputItem,
  type AskV2RailProgressItem,
  type AskV2RailSkill,
} from "./types";

import { selectFocusedCitedClaims } from "../../components/ask-puntt-cited-claims";
import { type GetProjectReviewsResponse } from "../../services/askPunttProjectFeed.types";
import { matchToolToConnectorId } from "../../utils/askPunttKnownConnectorTools";
import {
  projectReferenceDocumentRows,
  referenceRowDisplayName,
} from "../../utils/askPunttProjectReferenceDocuments";

const CONNECTOR_NAME_BY_ID = new Map(
  defaultAskPunttConnectors.map((connector) => [
    connector.connectorId,
    connector.name,
  ]),
);

const SKILL_NAME_BY_ID = new Map(
  defaultAskPunttSkills.map((skill) => [skill.skillId, skill.name]),
);

type ReviewListCardMeta = {
  title?: string;
  thumbnailUrl?: string | null;
  versionNumber?: number;
  aiCommentCount?: number;
  humanCommentCount?: number;
};

function reviewListCardMetaFromMessage(
  message: AskPunttMessage,
  reviewId: string,
): ReviewListCardMeta | null {
  // warholComponentsForChat does not dedupe atoms, so one message can carry
  // several review-list-cards. Collect them all before choosing, otherwise an
  // exact match sitting after an ambiguous pair is never compared.
  const cards: { id?: string; meta: ReviewListCardMeta }[] = [];
  for (const raw of message.components ?? []) {
    if (raw.componentId !== "ask-puntt.chat-atom.review-list-card") continue;
    const payload = raw.payload as {
      id?: string;
      name?: string;
      thumbnailUrl?: string;
      versionNumber?: number | null;
      aiCommentCount?: number;
      humanCommentCount?: number;
    };
    cards.push({
      id: payload.id,
      meta: {
        title: payload.name?.trim() || undefined,
        thumbnailUrl: payload.thumbnailUrl || undefined,
        versionNumber: payload.versionNumber ?? undefined,
        aiCommentCount: payload.aiCommentCount,
        humanCommentCount: payload.humanCommentCount,
      },
    });
  }

  // Pass 1: an exact id match always wins, wherever it sits in the list.
  const exact = cards.find((card) => card.id === reviewId);
  if (exact) return exact.meta;

  // Pass 2: ambiguity only decides the fallback — keep the sole card on this
  // message, and give up once two or more cards could be the one.
  // why: warhol invents payload.id before christo rewrites it to viewReviewId
  return cards.length === 1 ? cards[0].meta : null;
}

function hasCanvas(message: AskPunttMessage): boolean {
  const blocks = message.canvasContent?.blocks;
  return (
    (Array.isArray(blocks) && blocks.length > 0) ||
    message.canvasLoading === true
  );
}

function titleCaseSlug(slug: string): string {
  return slug
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function canvasTitle(message: AskPunttMessage, index: number): string {
  const outputType = message.canvasContent?.outputType;
  if (outputType) return titleCaseSlug(outputType);
  return `Document ${index + 1}`;
}

function collectConnectors(
  messages: readonly AskPunttMessage[],
): AskV2RailConnector[] {
  const seen = new Map<string, AskV2RailConnector>();
  for (const message of messages) {
    for (const step of message.agentSteps ?? []) {
      if (step.type !== "tool_call" && step.type !== "tool_result") continue;
      const toolName =
        step.type === "tool_result"
          ? step.toolName?.trim() || step.tool
          : step.tool;
      const args =
        step.type === "tool_call" && step.args && typeof step.args === "object"
          ? (step.args as Record<string, unknown>)
          : undefined;
      const id = matchToolToConnectorId(toolName, step.displayName ?? "", args);
      if (!id || seen.has(id)) continue;
      seen.set(id, {
        id,
        name: CONNECTOR_NAME_BY_ID.get(id) ?? id,
      });
    }
  }
  return Array.from(seen.values());
}

function collectDocuments(
  files: AskPunttProject["files"] | undefined,
): AskV2RailDocument[] {
  return projectReferenceDocumentRows(files).map((row) => {
    const name = referenceRowDisplayName(row.file, row.version);
    const resolvedType = row.file.type ?? "file";
    const source = row.version.source ?? row.version.key ?? "";
    return {
      id: `${row.file.fileId}-v${row.versionOrdinal}`,
      fileId: row.file.fileId,
      name,
      type: resolvedType,
      versionOrdinal: row.versionOrdinal,
      source,
      showVersionBadge: (row.file.versions?.length ?? 0) > 1,
    };
  });
}

function attachmentDisplayName(attachment: {
  name?: string;
  key: string;
}): string {
  const named = attachment.name?.trim();
  if (named) return named;
  return attachment.key.split("/").pop()?.trim() ?? "";
}

// why: warhol never files feed uploads onto project.files, so Context stayed empty
function collectAttachmentDocuments(
  messages: readonly AskPunttMessage[],
  filed: readonly AskV2RailDocument[],
): AskV2RailDocument[] {
  const seen = new Set<string>();
  for (const document of filed) {
    if (document.source) seen.add(document.source);
    if (document.name) seen.add(document.name);
  }
  const out: AskV2RailDocument[] = [];
  for (const message of messages) {
    for (const attachment of message.attachments ?? []) {
      const name = attachmentDisplayName(attachment);
      const source = attachment.source?.trim() ?? "";
      if (!name || seen.has(source) || seen.has(name)) continue;
      if (source) seen.add(source);
      seen.add(name);
      out.push({
        id: `attachment-${attachment.key}`,
        fileId: attachment.key,
        name,
        type: attachment.type,
        versionOrdinal: 1,
        source,
        showVersionBadge: false,
      });
    }
  }
  return out;
}

// why-not: no chat memory tier — AskPunttProjectInstruction.scope is project|enterprise only; chat-scoped lists are not on GET project/chat yet.
function collectMemories(
  instructions: readonly AskPunttProjectInstruction[] | undefined,
): AskV2RailMemory[] {
  const byScope = new Map<AskV2RailMemoryScope, string[]>();
  for (const instruction of instructions ?? []) {
    if (instruction.revokedAt != null) continue;
    const text = instruction.text?.trim();
    if (!text) continue;
    const scope = instruction.scope;
    if (scope !== "project" && scope !== "enterprise") continue;
    const bucket = byScope.get(scope) ?? [];
    bucket.push(text);
    byScope.set(scope, bucket);
  }
  const memories: AskV2RailMemory[] = [];
  for (const scope of ["project", "enterprise"] as const) {
    const texts = byScope.get(scope);
    if (texts?.length) {
      memories.push({ scope, text: texts.join(" ") });
    }
  }
  return memories;
}

/**
 * `chat.detectedSkill` is persisted by rembrandt as a skill *id*
 * (`compliance_review`, `custom_<uuid>`) — never a display label. Rendering it
 * verbatim leaks raw slugs and UUIDs into the rail chip, so only accept values
 * that cannot be an id (it differs from the workflow type and is not
 * slug-shaped).
 */
function detectedSkillLabel(
  detectedSkill: string | null | undefined,
  workflowType: string,
): string | undefined {
  const trimmed = detectedSkill?.trim();
  if (!trimmed || trimmed === workflowType) return undefined;
  if (/^[a-z0-9][a-z0-9_-]*$/.test(trimmed)) return undefined;
  return trimmed;
}

function collectSkills(
  chat?: Partial<Pick<AskPunttChat, "workflowType" | "detectedSkill">> | null,
): AskV2RailSkill[] {
  if (!chat?.workflowType || chat.workflowType === "general") return [];

  const workflowType = chat.workflowType;
  const label = detectedSkillLabel(chat.detectedSkill, workflowType);
  if (workflowType.startsWith("custom_")) {
    return [{ id: workflowType, name: label ?? "Custom skill" }];
  }

  const skillId =
    workflowType === "project" ? "project_workflow" : workflowType;
  const name =
    SKILL_NAME_BY_ID.get(skillId) ?? label ?? titleCaseSlug(workflowType);
  return [{ id: skillId, name }];
}

/** Default empty review state — not worth a progress rail section on its own. */
function isMeaningfulProgressStatus(
  status: string | null | undefined,
): boolean {
  const normalized = status?.trim().toLowerCase();
  return Boolean(normalized && normalized !== "idle");
}

function hasUserCommittedReviewStart(
  messages: readonly AskPunttMessage[],
): boolean {
  return messages.some(
    (message) =>
      message.role === "user" &&
      isReviewStartUserMessage(message.content ?? ""),
  );
}

type ReviewProgressChatInput = Pick<
  AskPunttChat,
  "reviewStatus" | "reviewStages"
> &
  Partial<Pick<AskPunttChat, "workflowType" | "reviewIds">>;

function isReviewItemStillInFlight(status: string | undefined): boolean {
  return status?.trim().toLowerCase() === "in_review";
}

function isReviewItemFinished(item: {
  status?: string;
  aiCommentCount?: number;
}): boolean {
  if (!isReviewItemStillInFlight(item.status)) return true;
  // why: listReviews can lag isRunningAIReview; comments on the board mean the
  // AI pass finished even when status is still in_review.
  return (item.aiCommentCount ?? 0) > 0;
}

function inReviewIdsFromApi(
  reviews?: GetProjectReviewsResponse | null,
): Set<string> {
  const ids = new Set<string>();
  for (const item of reviews?.reviews ?? []) {
    if (!isReviewItemStillInFlight(item.status)) continue;
    if ((item.aiCommentCount ?? 0) > 0) continue;
    ids.add(item.id);
    if (item.ticketId) ids.add(item.ticketId);
  }
  return ids;
}

function lastUserReviewStartIndex(
  messages: readonly AskPunttMessage[],
): number {
  let lastIndex = -1;
  messages.forEach((message, index) => {
    if (
      message.role === "user" &&
      isReviewStartUserMessage(message.content ?? "")
    ) {
      lastIndex = index;
    }
  });
  return lastIndex;
}

function hasFinishedReviewInMessages(
  messages: readonly AskPunttMessage[],
  reviews?: GetProjectReviewsResponse | null,
): boolean {
  const inReviewIds = inReviewIdsFromApi(reviews);
  const reviewStartIndex = lastUserReviewStartIndex(messages);

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== "assistant") continue;
    if (reviewStartIndex >= 0 && index < reviewStartIndex) break;

    for (const raw of message.components ?? []) {
      if (raw.componentId !== "ask-puntt.chat-atom.review-list-card") continue;
      const payload = raw.payload as {
        id?: string;
        aiCommentCount?: number;
      };
      const cardId = payload.id?.trim();
      if (!cardId || inReviewIds.has(cardId)) continue;
      if ((payload.aiCommentCount ?? 0) > 0) return true;
    }

    const reviewId = message.viewReviewId?.trim();
    if (!reviewId || inReviewIds.has(reviewId)) continue;
    if (message.status !== "pending") return true;
  }

  return false;
}

function hasActiveReviewWithoutComments(
  reviews?: GetProjectReviewsResponse | null,
): boolean {
  return (reviews?.reviews ?? []).some(
    (item) =>
      isReviewItemStillInFlight(item.status) &&
      (item.aiCommentCount ?? 0) === 0,
  );
}

function hasOnlyFinishedReviews(
  reviews?: GetProjectReviewsResponse | null,
): boolean {
  const reviewList = reviews?.reviews ?? [];
  if (reviewList.length === 0) return false;
  return reviewList.every(isReviewItemFinished);
}

function hasStaleInReviewWithComments(
  reviews?: GetProjectReviewsResponse | null,
): boolean {
  return (reviews?.reviews ?? []).some(
    (item) =>
      isReviewItemStillInFlight(item.status) && (item.aiCommentCount ?? 0) > 0,
  );
}

function messagesAfterLatestReviewStart(
  messages: readonly AskPunttMessage[],
): readonly AskPunttMessage[] {
  const startIndex = lastUserReviewStartIndex(messages);
  return startIndex >= 0 ? messages.slice(startIndex + 1) : messages;
}

function hasCompletedReviewEvidence(input: {
  chat?: ReviewProgressChatInput | null;
  messages?: readonly AskPunttMessage[];
  reviews?: GetProjectReviewsResponse | null;
}): boolean {
  if (hasActiveReviewWithoutComments(input.reviews)) return false;

  const messages = input.messages ?? [];
  const afterStart = messagesAfterLatestReviewStart(messages);

  if (hasFinishedReviewInMessages(afterStart, input.reviews)) return true;

  // Stale listReviews status: in_review with comments landed means finished.
  if (hasStaleInReviewWithComments(input.reviews)) return true;

  // Hydration on revisit: history/reviews can arrive before the full thread is
  // scanned; trust an all-finished reviews API only when no Start review cycle
  // is waiting on assistant output yet.
  if (lastUserReviewStartIndex(messages) < 0) {
    return hasOnlyFinishedReviews(input.reviews);
  }

  // Start review stays in history forever; a stale IN REVIEW chat status with
  // an all-finished reviews API means the cycle already completed on refresh.
  if (
    hasOnlyFinishedReviews(input.reviews) &&
    isMeaningfulProgressStatus(input.chat?.reviewStatus)
  ) {
    return true;
  }

  return false;
}

export function isReviewInFlight(input: {
  chat?: ReviewProgressChatInput | null;
  reviews?: GetProjectReviewsResponse | null;
  messages?: readonly AskPunttMessage[];
}): boolean {
  if (hasCompletedReviewEvidence(input)) return false;

  if (isMeaningfulProgressStatus(input.chat?.reviewStatus)) return true;
  if ((input.chat?.reviewStages?.length ?? 0) > 0) return true;
  if (
    (input.reviews?.reviews ?? []).some(
      (item) =>
        isReviewItemStillInFlight(item.status) &&
        (item.aiCommentCount ?? 0) === 0,
    )
  ) {
    return true;
  }
  if (hasUserCommittedReviewStart(input.messages ?? [])) {
    // why: the Start review user message stays in the thread forever; once the
    // reviews API shows a finished review, that message must not keep progress
    // running after a page refresh.
    if (!hasCompletedReviewEvidence(input)) return true;
  }

  return false;
}

export type AskV2ReviewScriptedProgressMode = "idle" | "running" | "completed";

export function getReviewScriptedProgressMode(input: {
  chat?: ReviewProgressChatInput | null;
  reviews?: GetProjectReviewsResponse | null;
  messages?: readonly AskPunttMessage[];
}): AskV2ReviewScriptedProgressMode {
  // why: Start review and stale reviewStatus persist in history after a review
  // finishes; finished reviews must win on refresh so the rail shows all done.
  if (hasCompletedReviewEvidence(input)) {
    return "completed";
  }

  if (isReviewInFlight(input)) return "running";

  const reviewList = input.reviews?.reviews ?? [];
  if (reviewList.some((item) => isReviewItemFinished(item))) {
    return "completed";
  }
  if ((input.chat?.reviewIds?.length ?? 0) > 0) {
    return "completed";
  }

  return "idle";
}

export function collectRailContent(input: {
  project?: Pick<
    AskPunttProject,
    "description" | "context" | "upcomingWork" | "files" | "instructions"
  > | null;
  chat?:
    | (Pick<
        AskPunttChat,
        "reviewStatus" | "reviewChecklist" | "reviewStages" | "projectData"
      > &
        Partial<
          Pick<AskPunttChat, "workflowType" | "detectedSkill" | "reviewIds">
        >)
    | null;
  messages?: readonly AskPunttMessage[];
  reviews?: GetProjectReviewsResponse | null;
}): AskV2RailContent {
  const messages = input.messages ?? [];
  const reviews = input.reviews?.reviews ?? [];
  const inReview = reviews.filter(
    (item) =>
      isReviewItemStillInFlight(item.status) &&
      (item.aiCommentCount ?? 0) === 0,
  );
  const finishedReviews = reviews.filter((item) => isReviewItemFinished(item));

  // why: Progress rows are client-side scripted steps (useAskV2ScriptedProgress); backend stages/checklist are not shown here.
  const progressMode = getReviewScriptedProgressMode(input);

  // Prefer whichever source carries a meaningful (non-"idle") status: the
  // reviews panel can sit at its "idle" default while the chat has already
  // moved to "IN REVIEW", and `??` would let that idle default shadow it.
  const reviewsStatus = input.reviews?.status;
  const chatStatus = input.chat?.reviewStatus ?? undefined;
  const progressStatus = isMeaningfulProgressStatus(reviewsStatus)
    ? reviewsStatus
    : isMeaningfulProgressStatus(chatStatus)
      ? chatStatus
      : (reviewsStatus ?? chatStatus);
  const progress =
    progressMode !== "idle"
      ? {
          status: isMeaningfulProgressStatus(progressStatus)
            ? progressStatus
            : undefined,
          items: [] as AskV2RailProgressItem[],
        }
      : null;

  const outputItems: AskV2RailOutputItem[] = [];
  const seenReviewIds = new Set<string>();
  // In-review reviews render as in-flight progress items, not output cards.
  // Seed their ids so a mirrored assistant message (viewReviewId/viewTicketId)
  // for the same review does not leak a duplicate "Review" card into output.
  for (const item of inReview) {
    seenReviewIds.add(item.id);
    if (item.ticketId) seenReviewIds.add(item.ticketId);
  }
  for (const item of finishedReviews) {
    seenReviewIds.add(item.id);
    if (item.ticketId) seenReviewIds.add(item.ticketId);
    outputItems.push({
      id: item.id,
      kind: "review",
      title: item.title ?? item.name,
      thumbnailUrl: item.thumbnailUrl,
      versionNumber: item.versionNumber,
      aiCommentCount: item.aiCommentCount,
      humanCommentCount: item.humanCommentCount,
    });
  }

  let canvasIndex = 0;
  for (const message of messages) {
    if (message.role !== "assistant") continue;
    const reviewId = message.viewReviewId;
    if (reviewId && !seenReviewIds.has(reviewId)) {
      seenReviewIds.add(reviewId);
      const atomMeta = reviewListCardMetaFromMessage(message, reviewId);
      outputItems.push({
        id: reviewId,
        kind: "review",
        title: atomMeta?.title ?? "Review",
        thumbnailUrl: atomMeta?.thumbnailUrl,
        versionNumber: atomMeta?.versionNumber,
        aiCommentCount: atomMeta?.aiCommentCount,
        humanCommentCount: atomMeta?.humanCommentCount,
        isGenerating: message.status === "pending",
      });
    }
    if (hasCanvas(message)) {
      outputItems.push({
        id: message._id,
        kind: "canvas",
        title: canvasTitle(message, canvasIndex),
        isGenerating: message.canvasLoading === true,
      });
      canvasIndex += 1;
    }
  }

  const output = outputItems.length > 0 ? { items: outputItems } : null;

  const overview =
    input.project?.description?.trim() ||
    input.project?.context?.trim() ||
    input.chat?.projectData?.description?.trim() ||
    "";
  const upcomingWork = input.project?.upcomingWork?.trim() || "";
  const memories = collectMemories(input.project?.instructions);
  const connectors = collectConnectors(messages);
  const filedDocuments = collectDocuments(input.project?.files);
  const documents = [
    ...filedDocuments,
    ...collectAttachmentDocuments(messages, filedDocuments),
  ];
  const skills = collectSkills(input.chat);
  // The most recent assistant answer that cited something, exactly as the
  // legacy chat's rail picks it (`selectFocusedCitedClaims`): rule chips first,
  // then the graph's relationships behind them.
  const citedClaims = [
    ...(selectFocusedCitedClaims(messages)?.citations ?? []),
  ];
  const context =
    overview ||
    upcomingWork ||
    memories.length > 0 ||
    connectors.length > 0 ||
    documents.length > 0 ||
    skills.length > 0 ||
    citedClaims.length > 0
      ? {
          overview: overview || undefined,
          upcomingWork: upcomingWork || undefined,
          memories,
          connectors,
          documents,
          skills,
          citedClaims,
        }
      : null;

  return { progress, output, context };
}

export function hasRailContent(content: AskV2RailContent): boolean {
  return (
    content.progress != null ||
    content.output != null ||
    content.context != null
  );
}
